import { CHAT_PUBLIC_SHARE_ACCESS_TOKEN_HEADER } from "../constants/chat-share.constants";
import type {
  IAddChatShareRecipientsBody,
  IBranchChatRequest,
  IBranchChatResponse,
  IChatByUuidResponse,
  IChatHistoryRequest,
  IChatHistoryResponse,
  IChatListObject,
  IChatListResponse,
  IChatPublicShare,
  IChatPublicShareStatusResponse,
  IChatPublicShareVerifyResponse,
  IChatShareResponse,
  IContextCondenseResult,
  ICreateChatPublicShareBody,
  ICreateChatPublicShareResponse,
  ICreateChatShareBody,
  IDownloadFileParams,
  IDownloadPreviewFileParams,
  IDownloadSharedChatFileParams,
  IFilePreviewSlidesResponse,
  IGetChatListParams,
  IGetFilePreviewSlidesParams,
  IGetPublicSharedChatParams,
  IIntegrationConnectPrompt,
  IMoveChatToFolderPayload,
  IPresignFileUploadParams,
  IPresignFileUploadResponse,
  IPublicSharedChatResponse,
  IRemoveChatShareRecipientsBody,
  IResolvedSharedChatLink,
  IResolveIntegrationConnectPromptPayload,
  ISearchAcrossChatsParams,
  ISharedChatResponse,
  ITranscribeAudioRequest,
  ITranscribeAudioResponse,
  IUpdateChatCurrentModelParams,
  IUpdateChatTitlePayload,
  TCrossChatSearchResponse,
} from "../types";
import { encodePathSegment } from "../utils/encode-path-segment";
import { buildChatFileDownloadPath } from "../utils/file";
import type { ApiClient } from "./client";

export class ChatApi {
  constructor(private readonly apiClient: ApiClient) {}

  async getChatList(params: IGetChatListParams): Promise<IChatListResponse> {
    const { limit, offset, search, folderId, kind } = params;
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
    const folderParam = folderId
      ? `&folderId=${encodeURIComponent(folderId)}`
      : "";
    // NOTE: NSAI-5678 — omitted for folder, archive and search listings, which
    // show assistant and regular chats side by side.
    const kindParam = kind ? `&kind=${encodeURIComponent(kind)}` : "";
    const response = await this.apiClient.get<IChatListResponse>(
      `/chats/list?limit=${limit}&offset=${offset}${searchParam}${folderParam}${kindParam}`,
    );
    return response.data;
  }

  // NOTE: Cross-chat semantic search. Cap on `q` and `limit` is enforced
  // server-side; clients should still debounce to avoid burning embedding
  // tokens on every keystroke. Returns plain-text snippets only — never
  // raw payloads from the encrypted DB column.
  async searchAcrossChats(
    params: ISearchAcrossChatsParams,
  ): Promise<TCrossChatSearchResponse> {
    const limitParam =
      params.limit !== undefined ? `&limit=${params.limit}` : "";
    const response = await this.apiClient.get<TCrossChatSearchResponse>(
      `/chats/search/semantic?q=${encodeURIComponent(params.q)}${limitParam}`,
    );
    return response.data;
  }

  async getChatHistory({
    chatId,
    limit,
    offset,
  }: IChatHistoryRequest): Promise<IChatHistoryResponse> {
    const response = await this.apiClient.get<IChatHistoryResponse>(
      `/chats/${encodePathSegment(chatId)}/history?limit=${limit}&offset=${offset}`,
    );
    return response.data;
  }

  async getChatByUuid(uuid: string): Promise<IChatByUuidResponse> {
    const response = await this.apiClient.get<IChatByUuidResponse>(
      `/chats/${encodePathSegment(uuid)}`,
    );
    return response.data;
  }

  async updateChatTitle({
    uuid,
    title,
  }: IUpdateChatTitlePayload): Promise<IChatListObject> {
    const response = await this.apiClient.put<IChatListObject>(
      `/chats/${encodePathSegment(uuid)}/title`,
      { title },
    );
    return response.data;
  }

  async updateChatCurrentModel({
    uuid,
    models,
    assistants,
  }: IUpdateChatCurrentModelParams): Promise<IChatListObject> {
    const response = await this.apiClient.put<IChatListObject>(
      `/chats/${encodePathSegment(uuid)}/update-model`,
      { models, assistants },
    );

    return response.data;
  }

  async archiveChat(uuid: string): Promise<IChatListObject> {
    const response = await this.apiClient.put<IChatListObject>(
      `/chats/${encodePathSegment(uuid)}/archive`,
    );
    return response.data;
  }

  async getArchivedChatList(
    params: IGetChatListParams,
  ): Promise<IChatListResponse> {
    const { limit, offset } = params;
    const response = await this.apiClient.get<IChatListResponse>(
      `/chats/list?limit=${limit}&offset=${offset}&isArchived=true`,
    );
    return response.data;
  }

  async pinChat(uuid: string): Promise<IChatListObject> {
    const response = await this.apiClient.post<IChatListObject>(
      `/chats/${encodePathSegment(uuid)}/pin`,
    );
    return response.data;
  }

  async moveChatToFolder({
    chatUuid,
    folderId,
  }: IMoveChatToFolderPayload): Promise<IChatListObject> {
    const response = await this.apiClient.post<IChatListObject>(
      `/chats/${encodePathSegment(chatUuid)}/folder`,
      { folderId },
    );
    return response.data;
  }

  // NOTE: NSAI-5681 — records how one inline "Connect <app>" card ended
  // (connected / failed / dismissed). A `connected` claim is verified against
  // the integrations table server-side, and an already-resolved card is never
  // overwritten. Answers with the message's cards as they now stand; the
  // outcome is also written to the transcript as a system row, so callers
  // refresh the history they render.
  async resolveIntegrationConnectPrompt({
    chatUuid,
    messageUuid,
    provider,
    resolution,
  }: IResolveIntegrationConnectPromptPayload): Promise<
    IIntegrationConnectPrompt[]
  > {
    const response = await this.apiClient.post<IIntegrationConnectPrompt[]>(
      `/chats/${encodePathSegment(chatUuid)}/messages/${encodePathSegment(messageUuid)}/integration-connect-prompt/resolve`,
      { provider, resolution },
    );
    return response.data;
  }

  async deleteChat(uuid: string): Promise<void> {
    await this.apiClient.post(`/chats/${encodePathSegment(uuid)}/delete`);
  }

  // NOTE: NSAI-5820 — condense this chat's earlier messages into a summary
  // now, on the user's request, instead of waiting for the automatic
  // message-count threshold. Always resolves: the endpoint answers 200 with a
  // status ("off", "nothing new", "already running"), because none of those is
  // a fault the user caused by pressing a button we offered them.
  async condenseChatContext(uuid: string): Promise<IContextCondenseResult> {
    const response = await this.apiClient.post<IContextCondenseResult>(
      `/chats/${encodePathSegment(uuid)}/condense-context`,
    );
    return response.data;
  }

  async branchChat({
    chatUuid,
    messageUuid,
    modelUuid,
  }: IBranchChatRequest & { chatUuid: string }): Promise<IBranchChatResponse> {
    const body: Record<string, string> = { messageUuid };
    if (modelUuid) {
      body.modelUuid = modelUuid;
    }
    const response = await this.apiClient.post<IBranchChatResponse>(
      `/chats/${encodePathSegment(chatUuid)}/branch`,
      body,
    );
    return response.data;
  }

  async transcribeAudio({
    audio,
    fileName = "audio.webm",
  }: ITranscribeAudioRequest): Promise<ITranscribeAudioResponse> {
    const formData = new FormData();

    if ("uri" in audio) {
      // NOTE: React Native FormData.append() has extended behavior (not in standard DOM API).
      // It accepts {uri, type, name} objects for file uploads, but TypeScript's
      // Blob type definition doesn't include this. We cast to bypass this limitation.
      formData.append("audio", audio as unknown as Blob);
    } else {
      formData.append("audio", audio, fileName);
    }

    const response = await this.apiClient.post<ITranscribeAudioResponse>(
      "/chats/transcribe",
      formData,
    );

    return response.data;
  }

  async presignFileUpload(
    params: IPresignFileUploadParams,
  ): Promise<IPresignFileUploadResponse> {
    const { signal, ...body } = params;
    const response = await this.apiClient.post<IPresignFileUploadResponse>(
      "/chats/files/presign",
      body,
      { ...(signal !== undefined && { signal }) },
    );

    return response.data;
  }

  async downloadFile(params: IDownloadFileParams): Promise<Blob> {
    const response = await this.apiClient.get<Blob>(
      buildChatFileDownloadPath({ fileUuid: params.fileUuid }),
      {
        responseType: "blob",
        onDownloadProgress: params.onProgress
          ? (event) =>
              params.onProgress?.({
                loaded: event.loaded,
                total: event.total,
              })
          : undefined,
        ...(params.signal !== undefined && { signal: params.signal }),
      },
    );

    return response.data;
  }

  async createChatShare(
    chatUuid: string,
    body: ICreateChatShareBody,
  ): Promise<IChatShareResponse> {
    const response = await this.apiClient.post<IChatShareResponse>(
      `/chats/${encodePathSegment(chatUuid)}/share`,
      body,
    );
    return response.data;
  }

  async getChatShare(chatUuid: string): Promise<IChatShareResponse | null> {
    const response = await this.apiClient.get<IChatShareResponse | null>(
      `/chats/${encodePathSegment(chatUuid)}/share`,
    );
    return response.data;
  }

  async revokeChatShare(chatUuid: string): Promise<void> {
    await this.apiClient.post(
      `/chats/${encodePathSegment(chatUuid)}/share/revoke`,
    );
  }

  async addChatShareRecipients(
    chatUuid: string,
    body: IAddChatShareRecipientsBody,
  ): Promise<IChatShareResponse> {
    const response = await this.apiClient.post<IChatShareResponse>(
      `/chats/${encodePathSegment(chatUuid)}/share/recipients`,
      body,
    );
    return response.data;
  }

  async removeChatShareRecipients(
    chatUuid: string,
    body: IRemoveChatShareRecipientsBody,
  ): Promise<IChatShareResponse> {
    const response = await this.apiClient.post<IChatShareResponse>(
      `/chats/${encodePathSegment(chatUuid)}/share/recipients/remove`,
      body,
    );
    return response.data;
  }

  async getSharedChat(
    shareToken: string,
    params: { limit: number; offset: number },
  ): Promise<ISharedChatResponse> {
    const response = await this.apiClient.get<ISharedChatResponse>(
      `/chats/shared/${encodePathSegment(shareToken)}?limit=${params.limit}&offset=${params.offset}`,
    );
    return response.data;
  }

  // NOTE: NSAI-6724 — a teammate who opens someone else's `/chat/<uuid>` gets a
  // 404; this asks whether that chat was shared with them and, if so, for the
  // token of the read-only view. Any refusal is a 404.
  async resolveSharedChatLink(
    chatUuid: string,
  ): Promise<IResolvedSharedChatLink> {
    const response = await this.apiClient.get<IResolvedSharedChatLink>(
      `/chats/${encodePathSegment(chatUuid)}/share/link`,
    );
    return response.data;
  }

  // NOTE: NSAI-5823 — public (no-account) chat share, owner management.

  async createChatPublicShare(
    chatUuid: string,
    body: ICreateChatPublicShareBody,
  ): Promise<ICreateChatPublicShareResponse> {
    const response = await this.apiClient.post<ICreateChatPublicShareResponse>(
      `/chats/${encodePathSegment(chatUuid)}/public-share`,
      body,
    );
    return response.data;
  }

  async getChatPublicShare(chatUuid: string): Promise<IChatPublicShare | null> {
    const response = await this.apiClient.get<IChatPublicShare | null>(
      `/chats/${encodePathSegment(chatUuid)}/public-share`,
    );
    return response.data;
  }

  async revokeChatPublicShare(chatUuid: string): Promise<void> {
    await this.apiClient.post(
      `/chats/${encodePathSegment(chatUuid)}/public-share/revoke`,
      {},
    );
  }

  // NOTE: NSAI-5823 — public chat share, anonymous viewer side. All of these opt
  // out of the client's session auth handling (skipAuthHandling): the viewer has
  // no NSAI session, and a 401 here means a bad password or a rotated one, not a
  // signed-out user — without the opt-out the client would clear storage and
  // bounce the visitor to the sign-in page.

  async getPublicSharedChatStatus(
    shareToken: string,
  ): Promise<IChatPublicShareStatusResponse> {
    const response = await this.apiClient.get<IChatPublicShareStatusResponse>(
      `/chats/public-shared/${encodePathSegment(shareToken)}`,
      { skipAuthHandling: true },
    );
    return response.data;
  }

  async verifyPublicSharedChatPassword(
    shareToken: string,
    password: string,
  ): Promise<IChatPublicShareVerifyResponse> {
    const response = await this.apiClient.post<IChatPublicShareVerifyResponse>(
      `/chats/public-shared/${encodePathSegment(shareToken)}/verify`,
      { password },
      { skipAuthHandling: true },
    );
    return response.data;
  }

  async getPublicSharedChat({
    shareToken,
    accessToken,
    limit,
    offset,
  }: IGetPublicSharedChatParams): Promise<IPublicSharedChatResponse> {
    const response = await this.apiClient.get<IPublicSharedChatResponse>(
      `/chats/public-shared/${encodePathSegment(shareToken)}/chat?limit=${limit}&offset=${offset}`,
      {
        skipAuthHandling: true,
        headers: { [CHAT_PUBLIC_SHARE_ACCESS_TOKEN_HEADER]: accessToken },
      },
    );
    return response.data;
  }

  async downloadPreviewFile(params: IDownloadPreviewFileParams): Promise<Blob> {
    const response = await this.apiClient.get<Blob>(
      `/chats/files/${encodePathSegment(params.fileUuid)}/preview`,
      {
        responseType: "blob",
        onDownloadProgress: params.onProgress
          ? (event) =>
              params.onProgress?.({
                loaded: event.loaded,
                total: event.total,
              })
          : undefined,
      },
    );

    return response.data;
  }

  // NOTE: Per-slide preview images for a generated presentation (NSAI-4418).
  // Mobile has no in-app .pptx/PDF renderer, so the server rasterizes the deck
  // to images and this returns them as ready-to-render data URLs for a
  // swipeable gallery.
  async getFilePreviewSlides(
    params: IGetFilePreviewSlidesParams,
  ): Promise<IFilePreviewSlidesResponse> {
    const response = await this.apiClient.get<IFilePreviewSlidesResponse>(
      `/chats/files/${encodePathSegment(params.fileUuid)}/preview-slides`,
    );
    return response.data;
  }

  async downloadSharedChatFile(
    params: IDownloadSharedChatFileParams,
  ): Promise<Blob> {
    const response = await this.apiClient.get<Blob>(
      `/chats/shared/${encodePathSegment(params.shareToken)}/files/${encodePathSegment(params.fileUuid)}/download`,
      {
        responseType: "blob",
        onDownloadProgress: params.onProgress
          ? (event) =>
              params.onProgress?.({
                loaded: event.loaded,
                total: event.total,
              })
          : undefined,
      },
    );

    return response.data;
  }
}
